import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column } from '@/components/admin/ListTable';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import {
  confirmPresidentCsv,
  CSV_ACTION_LABELS,
  presidentExportUrl,
  uploadPresidentCsv,
  type CsvJob,
  type CsvRowResult, csvSampleUrl } from '@/features/csv/api';


const COLUMNS: Column<CsvRowResult>[] = [
  { key: 'row_no', header: '行', render: (row) => row.row_no },
  { key: 'member_id', header: '会員ID', primary: true, render: (row) => row.member_id ?? '—' },
  {
    key: 'round',
    header: '種別・実施回',
    render: (row) => `${String(row.data.survey_type ?? '—')} / ${String(row.data.survey_round ?? '—')}`,
  },
  { key: 'action', header: '判定', render: (row) => CSV_ACTION_LABELS[row.action] },
  {
    key: 'errors',
    header: 'エラー・警告',
    render: (row) =>
      [...row.errors, ...row.warnings].length > 0 ? (
        <ul className="list-inside list-disc">
          {row.errors.map((e) => (
            <li key={e} className="text-wpnotice-error">
              {e}
            </li>
          ))}
          {row.warnings.map((w) => (
            <li key={w} className="text-wpnotice-warning">
              {w}
            </li>
          ))}
        </ul>
      ) : (
        '—'
      ),
  },
];

/**
 * 社長アンケートCSV取込（docs/csv-contracts.md「社長アンケート 回答取込」/ §12 M7 / D-146）。
 * 横持ち（1名1回の聴取=1行）5段階: 形式検証→会員突合→行プレビュー→理由入力確定→保存+監査。
 */
export function PresidentCsvImportPage(): ReactElement {
  const navigate = useNavigate();
  const [job, setJob] = useState<CsvJob | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onFileSelected(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    setJob(null);
    try {
      setJob(await uploadPresidentCsv(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'アップロードに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(): Promise<void> {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      setJob(await confirmPresidentCsv(job.id, reason));
    } catch (err) {
      setError(err instanceof Error ? err.message : '確定に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const hasErrors = (job?.summary?.error_count ?? 0) > 0;
  const isConfirmed = job?.status === 'confirmed';
  const warningCount =
    job?.rows.reduce((count, row) => count + (row.warnings.length > 0 ? 1 : 0), 0) ?? 0;

  return (
    <>
      <PageHeader
        title="社長アンケートCSV取込"
        description="社長から集めた回答を取り込みます。1名1回の聴取が1行になる形式です（Googleフォーム等で聴取した内容を貼り付けられます）。ファイルを選んだ時点では登録されません。"
        actions={
          <a
            href={presidentExportUrl()}
            className="btn-secondary"
          >
            CSV出力
          </a>
        }
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {isConfirmed && (
        <Notice kind="success" dismissible={false}>
          確定しました（新規 {job.summary?.create_count ?? 0} 件 / 更新{' '}
          {job.summary?.update_count ?? 0} 件）。
        </Notice>
      )}

      <MetaBox title="1. ファイルを選択">
        <p className="mb-2 text-wptext-muted">
          はじめての場合は、右上の「CSV出力」で現在の内容を出力し、そのファイルに書き足して取り込むのが確実です。
          まだ回答が無い場合は
          <a href={csvSampleUrl('president')} className="text-wpaccent hover:underline">
            サンプルCSVをダウンロード
          </a>
          して、記入例を置き換えてください。
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFileSelected(file);
          }}
        />
        <details className="mt-2">
          <summary className="cursor-pointer text-wpaccent">列の説明を見る</summary>
          <ul className="mt-1 list-inside list-disc text-xs text-wptext-muted">
            <li>
              会員No.・氏名（確認用）… どの受講生の回答かを特定します
            </li>
            <li>
              種別（<code>start_expectation</code>=START入口 /{' '}
              <code>growth_evaluation</code>=成長評価）・実施回（ラベル。例「3期 期中」）
            </li>
            <li>
              START入口の項目: 現在の評価・期待する状態・伸ばしてほしい領域
            </li>
            <li>
              成長評価の項目: 成長した点・満足度（1〜5）・課題。種別に合わない列は空のままにします
            </li>
            <li>聴取日・聴取担当者</li>
            <li>
              開示範囲 … 列があっても無視されます（事務局のみに固定）
            </li>
          </ul>
        </details>
      </MetaBox>

      {job && (
        <div className="mt-4">
          <MetaBox title="2. 取り込む前に内容を確認">
            <dl className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-wptext-muted">
              <span>
                新規 <strong className="text-wptext-strong">{job.summary?.create_count ?? 0}</strong>
              </span>
              <span>
                更新 <strong className="text-wptext-strong">{job.summary?.update_count ?? 0}</strong>
              </span>
              <span>
                変更なし{' '}
                <strong className="text-wptext-strong">{job.summary?.no_change_count ?? 0}</strong>
              </span>
              <span>
                警告 <strong className="text-wpnotice-warning">{warningCount}</strong>
              </span>
              <span>
                エラー{' '}
                <strong className="text-wpnotice-error">{job.summary?.error_count ?? 0}</strong>
              </span>
            </dl>
            <ListTable<CsvRowResult>
              caption="CSVプレビュー"
              rows={job.rows}
              columns={COLUMNS}
              rowKey={(row) => String(row.row_no)}
            />
          </MetaBox>

          {!isConfirmed && (
            <MetaBox title="3. 確定（理由必須・警告は理由付きで確定できます）">
              {hasErrors ? (
                <p className="text-wpnotice-error">
                  エラーが残っています。ファイルを修正して再アップロードしてください。
                </p>
              ) : (
                <>
                  <label htmlFor="president-csv-reason" className="mb-1.5 block text-wptext-muted">
                    取込理由（監査ログに記録されます）
                  </label>
                  <textarea
                    id="president-csv-reason"
                    required
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mb-3 w-full rounded-sm border border-wpborder px-3 py-2"
                  />
                  <button
                    type="button"
                    disabled={busy || !reason.trim()}
                    onClick={() => void onConfirm()}
                    className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
                  >
                    確定する
                  </button>
                </>
              )}
            </MetaBox>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate('/members')}
        className="mt-4 text-wpaccent hover:underline"
      >
        ← 会員一覧に戻る
      </button>
    </>
  );
}
